import Color from "color";
import { Link } from "gatsby";
import React, { useMemo, ReactElement } from "react";
import { motion, AnimatePresence } from "framer-motion";

import UTIcon, { UTIconProps } from "./icons";
import { usePartnerContext } from "../context/partner";
import { PartnerColors } from "../context/partner/types";

export type CardProps = {
  children?: React.ReactNode;
  padding?: string;
  cardClass?: string;
  className?: string;
  heading?: string;
  headingSize?: number;
  headingPadding?: string;
  icon?: UTIconProps["type"];
  iconSize?: number;
  iconExtra?: UTIconProps["type"];
  iconColor?: keyof PartnerColors;
  color?:
    | "grey"
    | "grey2"
    | "grey3"
    | "info"
    | "info2"
    | "warning"
    | "warning2"
    | "danger"
    | "danger2"
    | "success"
    | "success2"
    | "primary"
    | "primary2";
  overrideColors?: React.CSSProperties;
  style?: React.CSSProperties;
  headingStyle?: React.CSSProperties;
  link?: string;
  linkDisabled?: boolean;
  right?: React.ReactNode;
  footerButtons?: ReactElement;
  close?: () => void;
  accordion?: boolean;
  accordionOpen?: boolean;
  onAccordionHeaderClick?: () => void;
  animate?: boolean; // Enable/disable animations (default: true)
  onClick?: () => void;
};

export default function Card({
  children,
  style,
  link,
  linkDisabled,
  className = "",
  cardClass = "",
  color,
  heading,
  headingSize = 25,
  headingPadding = "p-3",
  overrideColors = {},
  padding = "p-5",
  icon = undefined,
  iconSize = 23,
  iconExtra = undefined,
  iconColor,
  right,
  footerButtons,
  headingStyle,
  close,
  accordion = false,
  accordionOpen: accordionOpenProp = false,
  onAccordionHeaderClick,
  animate = true,
  onClick,
}: CardProps) {
  const [accordionOpenState, setAccordionOpen] = React.useState<boolean>(false);

  const { colors } = usePartnerContext();

  const colorStyle: any = useMemo(() => {
    let borderColor = colors.border || colors.gray200 || "#e5e5e5";
    let backgroundColor = colors.white;
    let headerColor = {
      backgroundColor: colors.gray50 || colors.gray1,
      color: colors.textColor,
      borderColor: colors.border || colors.gray200 || "#e5e5e5",
    };
    let bodyColor = colors.textColor;
    let headingBorderColor = colors.gray300 || colors.gray3;

    if (color === "grey") {
      borderColor = colors.border || colors.gray200 || "#e5e5e5";
      headerColor = {
        backgroundColor: colors.gray50 || colors.gray1,
        color: colors.textColor,
        borderColor: colors.border || colors.gray200 || "#e5e5e5",
      };
    }

    if (color === "grey2") {
      backgroundColor = colors.gray50 || colors.gray1;
    }

    if (color === "grey3") {
      backgroundColor = colors.gray100 || colors.gray2;
    }

    if (color === "info") {
      borderColor = colors.infoBorder || Color(colors.info).lighten(0.3).hex();
      headerColor = {
        backgroundColor: colors.info,
        color: colors.infoText,
        borderColor: "transparent",
      };
    }

    if (color === "info2") {
      borderColor = colors.infoBorder || Color(colors.info).lighten(0.3).hex();
      backgroundColor = colors.infoLight || Color(colors.info).lighten(0.4).hex();
      headerColor = {
        backgroundColor: colors.info,
        color: colors.infoText,
        borderColor: "transparent",
      };
      bodyColor = colors.textColor;
    }

    if (color === "success") {
      borderColor = colors.successBorder || Color(colors.success).lighten(0.3).hex();
      headerColor = {
        backgroundColor: colors.success,
        color: colors.successText,
        borderColor: "transparent",
      };
    }

    if (color === "success2") {
      borderColor = colors.successBorder || Color(colors.success).lighten(0.3).hex();
      backgroundColor = colors.successLight || Color(colors.success).lighten(0.4).hex();
      bodyColor = colors.textColor;
      headerColor = {
        backgroundColor: colors.success,
        color: colors.successText,
        borderColor: "transparent",
      };
    }

    if (color === "warning") {
      borderColor = colors.warningBorder || Color(colors.warning).lighten(0.3).hex();
      headerColor = {
        backgroundColor: colors.warning,
        color: colors.warningText,
        borderColor: "transparent",
      };
      headingBorderColor = colors.warningText;
    }

    if (color === "warning2") {
      borderColor = colors.warningBorder || Color(colors.warning).lighten(0.3).hex();
      backgroundColor = colors.warningLight || Color(colors.warning).lighten(0.4).hex();
      bodyColor = colors.textColor;
      headerColor = {
        backgroundColor: colors.warning,
        color: colors.warningText,
        borderColor: "transparent",
      };
      headingBorderColor = colors.warningText;
    }

    if (color === "danger") {
      borderColor = colors.dangerBorder || Color(colors.danger).lighten(0.3).hex();
      bodyColor = colors.textColor;
      headerColor = {
        backgroundColor: colors.danger,
        color: colors.dangerText,
        borderColor: "transparent",
      };
    }

    if (color === "danger2") {
      borderColor = colors.dangerBorder || Color(colors.danger).lighten(0.3).hex();
      backgroundColor = colors.dangerLight || Color(colors.danger).lighten(0.4).hex();
      headerColor = {
        backgroundColor: colors.danger,
        color: colors.dangerText,
        borderColor: "transparent",
      };
    }

    if (color === "primary") {
      borderColor = colors.primaryLight || Color(colors.primary).lighten(0.3).hex();
      headerColor = {
        backgroundColor: colors.primary,
        color: colors.primaryText,
        borderColor: "transparent",
      };
      headingBorderColor = colors.primaryText;
    }

    if (color === "primary2") {
      // filled with primary and lettered in primaryText - the pair the partner
      // configured to contrast. A lightened primary under textColor isn't
      // guaranteed to be readable.
      borderColor = Color(colors.primary).darken(0.2).hex();
      backgroundColor = colors.primary;
      bodyColor = colors.primaryText;
      headerColor = {
        backgroundColor: colors.primary,
        color: colors.primaryText,
        borderColor: Color(colors.primary).darken(0.2).hex(),
      };
      headingBorderColor = colors.primaryText;
    }

    return {
      borderColor,
      backgroundColor,
      headerColor,
      bodyColor,
      headingBorderColor,
    };
  }, [color, colors]);

  const handleAccordionClick = () => {
    if (onAccordionHeaderClick) {
      onAccordionHeaderClick();
      return;
    }
    setAccordionOpen(!accordionOpenState);
  };

  const accordionOpen = accordionOpenProp || accordionOpenState;

  // Animation variants
  const isInteractive = Boolean(link || onClick);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const contentVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    expanded: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const card = (
    <motion.div
      initial={animate ? "hidden" : false}
      animate={animate ? "visible" : false}
      variants={animate ? cardVariants : undefined}
      whileHover={animate && isInteractive ? { y: -4, boxShadow: "0 4px 8px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" } : undefined}
      onClick={onClick}
      className={`${cardClass} rounded-lg overflow-hidden transition-shadow duration-200`}
      style={{
        ...{
          borderColor: colorStyle.borderColor as any,
          backgroundColor: colorStyle.backgroundColor,
          color: colorStyle.bodyColor,
          boxShadow: `0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)`,
          borderWidth: "1px",
          borderStyle: "solid",
          cursor: isInteractive ? "pointer" : "default",
        },
        ...overrideColors,
        ...style,
      }}
    >
      {(heading || right || accordion) && (
        <h2
          className={`flex text-lg font-semibold ${headingPadding} ${(!accordion || (accordion && accordionOpen)) && children && "border-b"} ${accordion && "cursor-pointer"}`}
          style={{
            ...colorStyle.headerColor,
            borderBottomColor: colorStyle.headerColor.borderColor !== "transparent" ? colorStyle.headerColor.borderColor : (colors.border || colors.gray200),
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            ...headingStyle
          }}
          onClick={accordion ? handleAccordionClick : undefined}
        >
          <div className={"flex items-center"}>
            {icon && (
              <span
                className={"mr-3 pr-3 border-r"}
                style={{
                  borderColor: colorStyle.headingBorderColor,
                  borderRightWidth: "1px",
                  borderRightStyle: "solid"
                }}
              >
                <UTIcon
                  type={icon}
                  extra={iconExtra}
                  color={iconColor ? colors[iconColor] : colorStyle.headerColor}
                  size={iconSize}
                />
              </span>
            )}
            <span style={{ fontSize: headingSize }}>{heading}</span>
          </div>
          <div className={"flex-1"}>{right}</div>
          <div className={"flex justify-end items-center"}>
            {close && (
              <span className={"cursor-pointer"} onClick={close}>
                <UTIcon
                  type={"close"}
                  color={iconColor ? colors[iconColor] : colorStyle.headerColor}
                  size={20}
                />
              </span>
            )}
            {accordion && (
              <span className={"cursor-pointer"} onClick={handleAccordionClick}>
                <UTIcon
                  type={accordionOpen ? "accordionOpen" : "accordionClosed"}
                  color={"black"}
                  size={20}
                />
              </span>
            )}
          </div>
        </h2>
      )}
      {!accordion && children && (
        <>
          <div className={`${padding} ${className}`}>{children}</div>
          {footerButtons && (
            <div className={"flex justify-end p-3"}>{footerButtons}</div>
          )}
        </>
      )}
      {accordion && (
        <AnimatePresence initial={false}>
          {accordionOpen && (
            <motion.div
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={animate ? contentVariants : undefined}
              style={{ overflow: "hidden" }}
            >
              <div className={`${padding} ${className}`}>{children}</div>
              {footerButtons && (
                <div className={"flex justify-end p-3"}>{footerButtons}</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  );

  if (link && !linkDisabled) {
    return <Link to={link}>{card}</Link>;
  }

  return card;
}
